import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { catalogService } from '../../../services/catalogService';
import { prospectService } from '../../../services/prospectService';

const RATE_PER_M3 = 450;

const QuoteCalculator = () => {
  const [catalogItems, setCatalogItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState('articles'); // 'articles' | 'volume'

  // Volume calculator state
  const [volumeArticleType, setVolumeArticleType] = useState('dimensions'); // 'dimensions' | 'meuble' | 'palette'
  const [longueur, setLongueur] = useState('');
  const [largeur, setLargeur] = useState('');
  const [hauteur, setHauteur] = useState('');
  const [unite, setUnite] = useState('cm');
  const [nbMeubles, setNbMeubles] = useState('');
  const [nbPalettes, setNbPalettes] = useState('');

  // Prospect fields
  const [prospectName, setProspectName] = useState('');
  const [prospectEmail, setProspectEmail] = useState('');
  const [prospectPhone, setProspectPhone] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    loadCatalogItems();
  }, []);

  useEffect(() => {
    if (searchTerm?.trim() === '') {
      setFilteredItems(catalogItems);
    } else {
      const search = searchTerm?.toLowerCase();
      const filtered = catalogItems?.filter(item =>
        item?.name?.toLowerCase()?.includes(search) || 
        item?.category?.toLowerCase()?.includes(search)
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, catalogItems]);

  const loadCatalogItems = async () => {
    try {
      const items = await catalogService?.getAll();
      setCatalogItems(items || []);
      setFilteredItems(items || []);
    } catch (err) {
      console.error('Error loading catalog:', err);
    } finally {
      setLoading(false);
    }
  };

  // --- Volume calculation ---
  const computedVolumeM3 = (() => {
    if (volumeArticleType !== 'dimensions') return null;
    const l = parseFloat(longueur) || 0;
    const w = parseFloat(largeur) || 0;
    const h = parseFloat(hauteur) || 0;
    if (l <= 0 || w <= 0 || h <= 0) return null;
    return unite === 'cm' ? (l * w * h) / 1_000_000 : l * w * h;
  })();

  const computedEstimatedPrice = computedVolumeM3 != null ? computedVolumeM3 * RATE_PER_M3 : null;

  // --- Article calculator handlers ---
  const handleAddItem = () => {
    if (!selectedCatalogItemId) {
      alert('Veuillez sélectionner un article');
      return;
    }

    const catalogItem = catalogItems?.find(item => item?.id === selectedCatalogItemId);
    if (!catalogItem) return;

    const existingItem = selectedItems?.find(item => item?.catalogItemId === catalogItem?.id);
    
    if (existingItem) {
      const updatedItems = selectedItems?.map(item =>
        item?.catalogItemId === catalogItem?.id
          ? { 
              ...item, 
              quantity: item?.quantity + quantity, 
              priceTotal: (item?.quantity + quantity) * item?.priceUnit 
            }
          : item
      );
      setSelectedItems(updatedItems);
    } else {
      const newItem = {
        catalogItemId: catalogItem?.id,
        catalogItemName: catalogItem?.name,
        catalogItemCategory: catalogItem?.category,
        quantity: quantity,
        priceUnit: catalogItem?.price,
        priceTotal: quantity * catalogItem?.price
      };
      setSelectedItems([...selectedItems, newItem]);
    }

    setSelectedCatalogItemId('');
    setQuantity(1);
  };

  const handleRemoveItem = (catalogItemId) => {
    const updatedItems = selectedItems?.filter(item => item?.catalogItemId !== catalogItemId);
    setSelectedItems(updatedItems);
  };

  const getTotalAmount = () => {
    return selectedItems?.reduce((sum, item) => sum + (item?.priceTotal || 0), 0);
  };

  const handleDownloadQuote = () => {
    const quoteText = `DEVIS ADT EXPRESS\n\n` +
      `Date: ${new Date()?.toLocaleDateString('fr-FR')}\n\n` +
      `Articles:\n` +
      selectedItems?.map(item => 
        `- ${item?.catalogItemName} (${item?.catalogItemCategory}) x${item?.quantity} = ${item?.priceTotal?.toFixed(2)}€`
      )?.join('\n') +
      `\n\nTOTAL: ${getTotalAmount()?.toFixed(2)}€\n\n` +
      `Ce devis est valable 30 jours.\n` +
      `Contact: +33 7 56 80 01 82`;

    const blob = new Blob([quoteText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devis-adt-${Date.now()}.txt`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSendQuoteEmail = async () => {
    // Validate prospect fields
    if (!prospectName?.trim()) {
      setEmailError('Veuillez entrer votre nom');
      return;
    }
    if (!prospectEmail?.trim()) {
      setEmailError('Veuillez entrer votre email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(prospectEmail)) {
      setEmailError('Email invalide');
      return;
    }
    if (selectedItems?.length === 0) {
      setEmailError('Veuillez ajouter au moins un article');
      return;
    }

    setSendingEmail(true);
    setEmailError('');
    setEmailSuccess(false);

    try {
      const result = await prospectService?.createProspectAndSendEmail({
        name: prospectName,
        email: prospectEmail,
        phone: prospectPhone,
        quoteItems: selectedItems,
        totalAmount: getTotalAmount()
      });

      if (result?.success) {
        setEmailSuccess(true);
        setEmailError('');
        // Reset form after 3 seconds
        setTimeout(() => {
          setProspectName('');
          setProspectEmail('');
          setProspectPhone('');
          setSelectedItems([]);
          setEmailSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error sending quote:', error);
      setEmailError('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative h-full"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 blur-2xl -z-10" />
      <div className="backdrop-blur-xl bg-white/80 rounded-2xl border border-white/50 shadow-xl p-8 sm:p-10 h-full">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <motion.div
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.6 }}
            className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
          >
            <Icon name="Calculator" size={28} color="white" />
          </motion.div>
          <div>
            <h3 className="text-2xl font-bold text-slate-800">Calculateur de Devis</h3>
            <p className="text-sm text-slate-600">Créez votre devis personnalisé</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex rounded-xl border-2 border-slate-200 overflow-hidden mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('articles')}
            className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all ${
              activeTab === 'articles' ?'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-inner' :'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Icon name="Package" size={16} />
            <span>Par article</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('volume')}
            className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all ${
              activeTab === 'volume' ?'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-inner' :'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Icon name="Box" size={16} />
            <span>Par volume (m³)</span>
          </button>
        </div>

        <div className="space-y-6">
          {/* Prospect Information Section — shared */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="User" size={22} className="text-blue-600" />
              <h4 className="text-lg font-bold text-slate-800">Vos Coordonnées</h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={prospectName}
                  onChange={(e) => setProspectName(e?.target?.value)}
                  placeholder="Jean Dupont"
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={prospectEmail}
                  onChange={(e) => setProspectEmail(e?.target?.value)}
                  placeholder="jean.dupont@example.com"
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  Téléphone (optionnel)
                </label>
                <input
                  type="tel"
                  value={prospectPhone}
                  onChange={(e) => setProspectPhone(e?.target?.value)}
                  placeholder="+33 6 12 34 56 78"
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                />
              </div>
            </div>
          </div>

          {/* ===== TAB: PAR ARTICLE ===== */}
          {activeTab === 'articles' && (
            <>
              {/* Search Bar */}
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">
                  Rechercher un article
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    placeholder="Tapez pour rechercher (ex: barrique, TV, matelas...)"
                    className="w-full px-5 py-4 pl-12 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/90 backdrop-blur-sm transition-all"
                  />
                  <Icon name="Search" size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
                {searchTerm && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-slate-600 mt-2 font-medium">
                    {filteredItems?.length} article(s) trouvé(s)
                  </motion.p>
                )}
              </div>

              {/* Dropdown + Quantity + Add Button */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                <div className="sm:col-span-7">
                  <label className="block text-sm font-semibold text-slate-800 mb-3">Sélectionner un article</label>
                  <select
                    value={selectedCatalogItemId}
                    onChange={(e) => setSelectedCatalogItemId(e?.target?.value)}
                    className="w-full px-5 py-4 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/90 backdrop-blur-sm transition-all"
                  >
                    <option value="">-- Choisir un article --</option>
                    {filteredItems?.map(item => (
                      <option key={item?.id} value={item?.id}>
                        {item?.name} - {item?.price?.toFixed(2)} € ({item?.category})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-sm font-semibold text-slate-800 mb-3">Quantité</label>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e?.target?.value) || 1)}
                    className="w-full px-5 py-4 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/90 backdrop-blur-sm transition-all"
                  />
                </div>
                <div className="sm:col-span-2 flex items-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={handleAddItem}
                    disabled={!selectedCatalogItemId}
                    className="w-full py-4 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[52px] shadow-lg hover:shadow-xl font-semibold"
                  >
                    <Icon name="Plus" size={22} />
                    <span className="hidden sm:inline">Ajouter</span>
                  </motion.button>
                </div>
              </div>

              {/* Selected Items Table */}
              {selectedItems?.length > 0 ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border-2 border-slate-200 rounded-2xl overflow-hidden shadow-lg">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                    <h4 className="font-bold text-white flex items-center space-x-3 text-lg">
                      <Icon name="Package" size={22} />
                      <span>Articles sélectionnés</span>
                    </h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b-2 border-slate-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-bold text-slate-800">Article</th>
                          <th className="px-6 py-4 text-left text-sm font-bold text-slate-800">Catégorie</th>
                          <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">Prix unitaire</th>
                          <th className="px-6 py-4 text-center text-sm font-bold text-slate-800">Qté</th>
                          <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">Total</th>
                          <th className="px-6 py-4 text-center text-sm font-bold text-slate-800">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white/90">
                        {selectedItems?.map(item => (
                          <motion.tr key={item?.catalogItemId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-blue-50/50 transition-colors">
                            <td className="px-6 py-4 text-sm font-semibold text-slate-800">{item?.catalogItemName}</td>
                            <td className="px-6 py-4 text-sm text-slate-600">{item?.catalogItemCategory}</td>
                            <td className="px-6 py-4 text-sm text-right text-slate-800">{item?.priceUnit?.toFixed(2)} €</td>
                            <td className="px-6 py-4 text-sm text-center font-semibold text-slate-800">{item?.quantity}</td>
                            <td className="px-6 py-4 text-sm text-right font-bold text-blue-600">{item?.priceTotal?.toFixed(2)} €</td>
                            <td className="px-6 py-4 text-center">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => handleRemoveItem(item?.catalogItemId)}
                                className="text-red-500 hover:text-red-700 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer"
                              >
                                <Icon name="Trash2" size={20} />
                              </motion.button>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gradient-to-r from-blue-50 to-purple-50 border-t-2 border-slate-200">
                        <tr>
                          <td colSpan="4" className="px-6 py-5 text-right text-lg font-bold text-slate-800">TOTAL:</td>
                          <td className="px-6 py-5 text-right text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{getTotalAmount()?.toFixed(2)} €</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 p-6">
                    {emailSuccess && (
                      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center space-x-3">
                        <Icon name="CheckCircle" size={24} className="text-green-600" />
                        <div>
                          <p className="font-bold text-green-800">Devis envoyé avec succès !</p>
                          <p className="text-sm text-green-700">Consultez votre boîte email ({prospectEmail})</p>
                        </div>
                      </motion.div>
                    )}
                    {emailError && (
                      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center space-x-3">
                        <Icon name="AlertCircle" size={24} className="text-red-600" />
                        <p className="text-red-800 font-semibold">{emailError}</p>
                      </motion.div>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSendQuoteEmail}
                      disabled={sendingEmail}
                      className="w-full py-5 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl font-bold text-lg"
                    >
                      {sendingEmail ? (
                        <><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div><span>Envoi en cours...</span></>
                      ) : (
                        <><Icon name="Mail" size={24} /><span>📧 Recevoir le Devis par Email</span></>
                      )}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDownloadQuote}
                      className="w-full py-4 px-6 bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-xl hover:from-slate-700 hover:to-slate-800 transition-all flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl font-semibold"
                    >
                      <Icon name="Download" size={22} />
                      <span>Télécharger le Devis (TXT)</span>
                    </motion.button>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12 text-slate-500 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200">
                  <Icon name="Package" size={56} className="mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-semibold mb-1">Aucun article sélectionné</p>
                  <p className="text-sm">Ajoutez des articles pour calculer votre devis</p>
                </motion.div>
              )}
            </>
          )}

          {/* ===== TAB: PAR VOLUME ===== */}
          {activeTab === 'volume' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-5">
              {/* Info banner */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  Calculez le volume de votre envoi et obtenez une estimation instantanée au tarif de <strong>{RATE_PER_M3} €/m³</strong>. Pour les meubles et palettes, le volume est calculé sur devis.
                </p>
              </div>

              {/* Article type selector */}
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">Type d'envoi</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'dimensions', label: 'Dimensions', icon: 'Ruler' },
                    { value: 'meuble', label: 'Meuble(s)', icon: 'Armchair' },
                    { value: 'palette', label: 'Palette(s)', icon: 'Layers' },
                  ]?.map(opt => (
                    <button
                      key={opt?.value}
                      type="button"
                      onClick={() => setVolumeArticleType(opt?.value)}
                      className={`py-3 px-3 rounded-xl border-2 text-sm font-semibold flex flex-col items-center space-y-1 transition-all ${
                        volumeArticleType === opt?.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50/50'
                      }`}
                    >
                      <Icon name={opt?.icon} size={18} />
                      <span>{opt?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Dimensions fields */}
              {volumeArticleType === 'dimensions' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  {/* Unit selector */}
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-semibold text-slate-700">Unité :</span>
                    <div className="flex rounded-lg border-2 border-slate-200 overflow-hidden">
                      {['cm', 'm']?.map(u => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setUnite(u)}
                          className={`px-5 py-2 text-sm font-semibold transition-all ${
                            unite === u ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* L × W × H */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Longueur', value: longueur, setter: setLongueur },
                      { label: 'Largeur', value: largeur, setter: setLargeur },
                      { label: 'Hauteur', value: hauteur, setter: setHauteur },
                    ]?.map(field => (
                      <div key={field?.label}>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {field?.label} <span className="text-slate-400 font-normal">({unite})</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={field?.value}
                          onChange={(e) => field?.setter(e?.target?.value)}
                          placeholder="0"
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all text-center font-semibold"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Result card */}
                  <motion.div
                    animate={{ scale: computedVolumeM3 != null ? 1 : 0.98, opacity: computedVolumeM3 != null ? 1 : 0.6 }}
                    className={`rounded-2xl border-2 p-6 ${computedVolumeM3 != null ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-purple-50' : 'border-slate-200 bg-slate-50'}`}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <Icon name="Box" size={20} color="white" />
                      </div>
                      <h5 className="font-bold text-slate-800 text-lg">Résultat du calcul</h5>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                        <p className="text-xs text-slate-500 font-medium mb-1">Volume calculé</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {computedVolumeM3 != null ? `${computedVolumeM3?.toFixed(3)} m³` : '— m³'}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                        <p className="text-xs text-slate-500 font-medium mb-1">Tarif estimé ({RATE_PER_M3} €/m³)</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {computedEstimatedPrice != null ? `${computedEstimatedPrice?.toFixed(2)} €` : '— €'}
                        </p>
                      </div>
                    </div>
                    {computedVolumeM3 != null && (
                      <p className="text-xs text-slate-500 mt-3 text-center">
                        * Estimation indicative. Le tarif final peut varier selon les conditions d'enlèvement.
                      </p>
                    )}
                  </motion.div>
                </motion.div>
              )}

              {/* Meuble fields */}
              {volumeArticleType === 'meuble' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Nombre de meubles</label>
                    <input
                      type="number"
                      min="1"
                      value={nbMeubles}
                      onChange={(e) => setNbMeubles(e?.target?.value)}
                      placeholder="Ex : 3"
                      className="w-full px-5 py-4 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                    />
                  </div>
                  <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 flex items-start space-x-4">
                    <div className="w-10 h-10 bg-amber-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Info" size={20} color="white" />
                    </div>
                    <div>
                      <p className="font-bold text-amber-900 mb-1">Volume calculé sur devis</p>
                      <p className="text-sm text-amber-800">
                        {nbMeubles && parseInt(nbMeubles) > 0
                          ? `${parseInt(nbMeubles)} meuble(s) — notre équipe vous contactera pour évaluer le volume exact et vous fournir un tarif précis.`
                          : 'Indiquez le nombre de meubles. Notre équipe vous contactera pour évaluer le volume exact.'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Palette fields */}
              {volumeArticleType === 'palette' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Nombre de palettes</label>
                    <input
                      type="number"
                      min="1"
                      value={nbPalettes}
                      onChange={(e) => setNbPalettes(e?.target?.value)}
                      placeholder="Ex : 2"
                      className="w-full px-5 py-4 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                    />
                  </div>
                  <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 flex items-start space-x-4">
                    <div className="w-10 h-10 bg-amber-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Info" size={20} color="white" />
                    </div>
                    <div>
                      <p className="font-bold text-amber-900 mb-1">Volume calculé sur devis</p>
                      <p className="text-sm text-amber-800">
                        {nbPalettes && parseInt(nbPalettes) > 0
                          ? `${parseInt(nbPalettes)} palette(s) — notre équipe vous contactera pour évaluer le volume exact et vous fournir un tarif précis.`
                          : 'Indiquez le nombre de palettes. Notre équipe vous contactera pour évaluer le volume exact.'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* CTA: contact */}
              <div className="pt-2">
                <a
                  href="tel:+33756800182"
                  className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl font-bold text-base"
                >
                  <Icon name="Phone" size={20} />
                  <span>Nous contacter pour un devis précis</span>
                </a>
                <p className="text-xs text-slate-500 text-center mt-2">+33 7 56 80 01 82 — Lun–Sam 9h–19h</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default QuoteCalculator;